import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function TechnicianRegister() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    verify_password: '',
    address: '',
    state: '',
    lga: '',
    technician_type: ''
  });
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (formData.state) {
      fetchLgas(formData.state);
    }
  }, [formData.state]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/locations/states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Failed to fetch states');
    }
  };

  const fetchLgas = async (state) => {
    try {
      const response = await axios.get(`${API}/locations/lgas/${state}`);
      setLgas(response.data.lgas);
    } catch (error) {
      console.error('Failed to fetch LGAs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.verify_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register-staff`, {
        ...formData,
        role: 'technician'
      });
      setSubmitted(true);
      toast.success('Registration submitted! Awaiting approval.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white border-2 border-border rounded-sm shadow-hard p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-heading font-bold text-foreground mb-2">Registration Submitted!</h2>
          <p className="text-muted-foreground mb-6">
            Your application is pending approval. You will be notified once an admin or secretary approves your account.
          </p>
          <Link to="/login">
            <Button className="btn-primary">Back to Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border-2 border-border rounded-sm shadow-hard p-8">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
              alt="Excel Era Auto Care" 
              className="h-16 w-16 object-contain mx-auto mb-4"
            />
            <h1 className="text-3xl font-heading font-bold text-foreground">Technician Registration</h1>
            <p className="text-muted-foreground font-medium mt-2">Join our team of expert technicians</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Full Name</Label>
              <Input
                required
                className="input-field mt-2"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Email</Label>
              <Input
                type="email"
                required
                className="input-field mt-2"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Phone Number</Label>
              <Input
                type="tel"
                required
                className="input-field mt-2"
                placeholder="+234 XXX XXX XXXX"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Technician Type</Label>
              <Select value={formData.technician_type} onValueChange={(v) => setFormData({ ...formData, technician_type: v })}>
                <SelectTrigger className="input-field mt-2">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-house">In-House (Full-time)</SelectItem>
                  <SelectItem value="outsourced">Outsourced (Contract)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">State</Label>
                <Select value={formData.state} onValueChange={(v) => setFormData({ ...formData, state: v, lga: '' })}>
                  <SelectTrigger className="input-field mt-2">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {states.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">LGA</Label>
                <Select value={formData.lga} onValueChange={(v) => setFormData({ ...formData, lga: v })} disabled={!formData.state}>
                  <SelectTrigger className="input-field mt-2">
                    <SelectValue placeholder="Select LGA" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {lgas.map(lga => (
                      <SelectItem key={lga} value={lga}>{lga}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Address</Label>
              <Input
                className="input-field mt-2"
                placeholder="Your detailed address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Password</Label>
              <Input
                type="password"
                required
                className="input-field mt-2"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <div>
              <Label className="font-bold uppercase text-sm tracking-wide">Confirm Password</Label>
              <Input
                type="password"
                required
                className="input-field mt-2"
                value={formData.verify_password}
                onChange={(e) => setFormData({ ...formData, verify_password: e.target.value })}
              />
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-sm p-4 text-sm">
              <p className="font-bold text-yellow-800 mb-1">Note:</p>
              <p className="text-yellow-700">Your registration will be reviewed by an admin or secretary before activation.</p>
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.technician_type}
              className="btn-primary w-full h-12 text-lg"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground font-medium">
              Already registered?{' '}
              <Link to="/login" className="text-primary font-bold hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
